import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import type { GoogleConfigResponse, SessionResponse } from "@learn-flow/contracts";

import { ApiError, apiRequest } from "../../lib/api.js";
import { sessionQueryKey } from "./session.js";

type GoogleCredentialResponse = { credential?: string };

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize(input: {
            client_id: string;
            callback: (response: GoogleCredentialResponse) => void;
            auto_select: boolean;
            ux_mode: "popup";
          }): void;
          renderButton(
            parent: HTMLElement,
            options: {
              type: "standard";
              theme: "outline";
              size: "large";
              text: "continue_with";
              shape: "rectangular";
              width: number;
            },
          ): void;
          cancel(): void;
        };
      };
    };
  }
}

let googleScriptPromise: Promise<void> | undefined;

export function GoogleSignIn({
  disclosureId,
}: {
  disclosureId?: string;
} = {}): React.JSX.Element | null {
  const queryClient = useQueryClient();
  const buttonHost = useRef<HTMLDivElement>(null);
  const proofPasswordInput = useRef<HTMLInputElement>(null);
  const authenticationPending = useRef(false);
  const [sdkError, setSdkError] = useState("");
  const [sdkUnavailable, setSdkUnavailable] = useState(false);
  const [sdkAttempt, setSdkAttempt] = useState(0);
  const [credentialForProof, setCredentialForProof] = useState("");
  const [password, setPassword] = useState("");
  const [proofError, setProofError] = useState("");
  const config = useQuery({
    queryKey: ["google-auth-config"],
    queryFn: () => apiRequest<GoogleConfigResponse>("/api/v1/auth/google/config"),
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });
  const authenticate = useMutation({
    mutationFn: (input: { credential: string; password?: string; termsAccepted?: true }) =>
      apiRequest<SessionResponse>("/api/v1/auth/google", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: (session) => {
      setCredentialForProof("");
      setPassword("");
      setProofError("");
      setSdkError("");
      queryClient.setQueryData(sessionQueryKey, session);
    },
    onError: (error, input) => {
      if (error instanceof ApiError && error.problem?.code === "GOOGLE_LINK_PASSWORD_REQUIRED") {
        setCredentialForProof(input.credential);
        setProofError("");
        setSdkError("");
        return;
      }
      if (error instanceof ApiError && error.problem?.code === "GOOGLE_LINK_PASSWORD_INCORRECT") {
        setCredentialForProof(input.credential);
        setPassword("");
        setProofError("That password is incorrect. Try your existing MemoKite password again.");
        setSdkError("");
        return;
      }
      setPassword("");
      setProofError("");
      setSdkError(googleAuthenticationErrorMessage(error));
    },
    onSettled: () => {
      authenticationPending.current = false;
    },
  });
  const authenticateRef = useRef(authenticate);
  authenticateRef.current = authenticate;

  useEffect(() => {
    if (!credentialForProof) return;
    const frame = requestAnimationFrame(() => proofPasswordInput.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [credentialForProof, proofError]);

  useEffect(() => {
    if (!config.data?.enabled || !config.data.clientId || !buttonHost.current) return;
    let active = true;
    const host = buttonHost.current;
    void loadGoogleScript()
      .then(() => {
        if (!active) return;
        if (!window.google) {
          throw new Error("Google Identity Services did not initialize.");
        }
        setSdkUnavailable(false);
        setSdkError("");
        host.replaceChildren();
        window.google.accounts.id.initialize({
          client_id: config.data.clientId ?? "",
          auto_select: false,
          ux_mode: "popup",
          callback: (response) => {
            if (!response.credential) {
              setSdkError(
                "Google did not return a sign-in credential. Reopen the Google chooser and try again.",
              );
              return;
            }
            if (authenticationPending.current) return;
            authenticationPending.current = true;
            setSdkError("");
            authenticateRef.current.mutate({
              credential: response.credential,
              termsAccepted: true,
            });
          },
        });
        window.google.accounts.id.renderButton(host, {
          type: "standard",
          theme: "outline",
          size: "large",
          text: "continue_with",
          shape: "rectangular",
          width: Math.min(360, Math.max(240, host.clientWidth || 320)),
        });
        if (disclosureId) {
          host
            .querySelector<HTMLElement>('button, [role="button"], iframe:not([aria-hidden="true"])')
            ?.setAttribute("aria-describedby", disclosureId);
        }
      })
      .catch(() => {
        if (!active) return;
        setSdkUnavailable(true);
        setSdkError("Google sign-in is temporarily unavailable. Please try again.");
      });
    return () => {
      active = false;
      host.replaceChildren();
    };
  }, [config.data?.clientId, config.data?.enabled, disclosureId, sdkAttempt]);

  const retryGoogle = (): void => {
    setSdkError("");
    setSdkUnavailable(false);
    if (config.isError) {
      void config.refetch();
      return;
    }
    resetGoogleScript();
    setSdkAttempt((attempt) => attempt + 1);
  };
  const displayedError =
    sdkError ||
    (config.isError
      ? "MemoKite could not load its Google sign-in configuration. Check your connection and try again."
      : config.data?.enabled === false
        ? "Google sign-in is not configured for this MemoKite environment."
        : "");

  return (
    <div className="google-auth-section">
      <div className="auth-divider" aria-hidden="true">
        <span>or</span>
      </div>
      {config.data?.enabled === false ? null : (
        <div
          ref={buttonHost}
          className={
            authenticate.isPending ? "google-button-host is-pending" : "google-button-host"
          }
          aria-label="Continue with Google"
          aria-describedby={disclosureId}
          aria-busy={authenticate.isPending}
        />
      )}
      {config.isError || sdkUnavailable ? (
        <button type="button" className="soft-button google-retry-button" onClick={retryGoogle}>
          Try Google sign-in again
        </button>
      ) : null}
      {credentialForProof ? (
        <form
          className="google-link-proof"
          onSubmit={(event) => {
            event.preventDefault();
            if (!password || authenticationPending.current) return;
            authenticationPending.current = true;
            authenticate.mutate({
              credential: credentialForProof,
              password,
              termsAccepted: true,
            });
          }}
        >
          <p>
            This Google address matches an existing MemoKite account. Enter its password to link
            them safely.
          </p>
          <label htmlFor="google-link-password">
            Existing MemoKite password
            <input
              ref={proofPasswordInput}
              id="google-link-password"
              type="password"
              autoComplete="current-password"
              value={password}
              aria-invalid={proofError ? "true" : undefined}
              aria-describedby={proofError ? "google-link-password-error" : undefined}
              disabled={authenticate.isPending}
              onChange={(event) => {
                setPassword(event.target.value);
                setProofError("");
              }}
            />
          </label>
          {proofError ? (
            <p id="google-link-password-error" className="field-error" role="alert">
              {proofError}
            </p>
          ) : null}
          <button
            type="submit"
            className="soft-button"
            disabled={!password || authenticate.isPending}
          >
            {authenticate.isPending ? "Linking…" : "Link and continue"}
          </button>
        </form>
      ) : null}
      <div className="auth-message" aria-live="assertive" aria-atomic="true">
        {displayedError ? (
          <p className="login-error" role="alert">
            {displayedError}
          </p>
        ) : null}
      </div>
    </div>
  );
}

function googleAuthenticationErrorMessage(error: unknown): string {
  if (!(error instanceof ApiError)) {
    return "MemoKite could not be reached. Check your connection and try Google sign-in again.";
  }
  if (error.status === 429) {
    return "Too many sign-in attempts. Please wait and try again.";
  }
  switch (error.problem?.code) {
    case "UNTRUSTED_ORIGIN":
      return "This MemoKite address is not approved for sign-in. Open the configured MemoKite URL and try again.";
    case "INVALID_GOOGLE_CREDENTIAL":
      return "Google returned an invalid or expired sign-in credential. Reopen the Google chooser and try again.";
    case "GOOGLE_TERMS_ACCEPTANCE_REQUIRED":
      return "MemoKite could not record Terms acceptance for this Google account. Please try again.";
    case "EMAIL_VERIFICATION_REQUIRED":
      return "Verify your email before linking Google, then try again.";
    default:
      return "MemoKite could not complete Google sign-in. Please try again.";
  }
}

function loadGoogleScript(): Promise<void> {
  if (window.google) return Promise.resolve();
  if (googleScriptPromise) return googleScriptPromise;
  googleScriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      'script[src="https://accounts.google.com/gsi/client"]',
    );
    const script = existing ?? document.createElement("script");
    const loaded = (): void => resolve();
    const failed = (): void => {
      googleScriptPromise = undefined;
      script.remove();
      reject(new Error("Google Identity Services failed to load."));
    };
    script.addEventListener("load", loaded, { once: true });
    script.addEventListener("error", failed, { once: true });
    if (!existing) {
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      document.head.append(script);
    }
  });
  return googleScriptPromise;
}

function resetGoogleScript(): void {
  googleScriptPromise = undefined;
  document
    .querySelector<HTMLScriptElement>('script[src="https://accounts.google.com/gsi/client"]')
    ?.remove();
  delete window.google;
}
